import { collection, getDocs, getFirestore, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getFirebaseApp } from '../firebaseApp';

export interface CachedContact {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  company?: string;
}

let _db: ReturnType<typeof getFirestore> | undefined;
function getDb() {
  if (!_db) _db = getFirestore(getFirebaseApp(), 'contacts');
  return _db;
}

class ContactsCacheService {
  private static instance: ContactsCacheService | null = null;
  private cache = new Map<string, CachedContact>();
  private loadPromise: Promise<void> | null = null;
  private loaded = false;
  private unsubscribe: Unsubscribe | null = null;

  private constructor() {}

  static getInstance(): ContactsCacheService {
    if (!ContactsCacheService.instance) {
      ContactsCacheService.instance = new ContactsCacheService();
    }
    return ContactsCacheService.instance;
  }

  async ensureLoaded(): Promise<void> {
    if (this.loaded) return;
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = this._load().finally(() => { this.loadPromise = null; });
    return this.loadPromise;
  }

  private async _load(): Promise<void> {
    try {
      const snap = await getDocs(collection(getDb(), 'contacts'));
      snap.docs.forEach(d => this.cache.set(d.id, this._map(d.id, d.data())));
      this.loaded = true;
    } catch (e) {
      console.warn('[contactsCache] load failed:', e);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _map(id: string, d: Record<string, any>): CachedContact {
    const firstName = d.firstName || '';
    const lastName  = d.lastName  || '';
    return {
      id,
      name:      d.name || `${firstName} ${lastName}`.trim() || id,
      firstName: firstName || undefined,
      lastName:  lastName  || undefined,
      email:     d.email   || undefined,
      phone:     d.phone   || undefined,
      avatar:    d.avatar  || undefined,
      company:   d.company || undefined,
    };
  }

  subscribeToUpdates(): void {
    if (this.unsubscribe) return;
    this.unsubscribe = onSnapshot(collection(getDb(), 'contacts'), snap => {
      snap.docChanges().forEach(change => {
        if (change.type === 'removed') {
          this.cache.delete(change.doc.id);
        } else {
          this.cache.set(change.doc.id, this._map(change.doc.id, change.doc.data()));
        }
      });
      this.loaded = true;
    });
  }

  unsubscribeFromUpdates(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  getById(contactId: string): CachedContact | undefined {
    return this.cache.get(contactId);
  }

  getAvatar(contactId: string): string | undefined {
    return this.cache.get(contactId)?.avatar;
  }

  getName(contactId: string): string | undefined {
    return this.cache.get(contactId)?.name;
  }
}

export const contactsCache = ContactsCacheService.getInstance();
